import { ExtractErrorInfo } from "components/shared/ExtractErrorInfo";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api } from "Config/Api";
import { API_ENDPOINTS } from "Config/apiConfig";
import { AppType } from "types/appType";
import { AssetType } from "types/assetType";
import { IncidentType } from "types/incidentType";
import { ErrorType } from "types/errorType";
import { RootState } from "store/store";
import { ApiResponse } from "utils/fetchDataUtils";
import { Status } from "Config/Enum/statusEnum";

export interface AppTypeWithAssetType extends AppType {
  loading: boolean;
  error: ErrorType | null;
  assets: AssetType[];
  minSearchLength: number;
}

const initialState: AppTypeWithAssetType = {
  error: null,
  loading: false,
  assets: [],
  minSearchLength: 3,
};

interface assetProps {
  userId: string;
  organisationId: string;
  searchValue: string;
}

export const fetchAssets = createAsyncThunk<AssetType[], assetProps>(
  "assets/fetchAssets",
  async ({ userId, organisationId, searchValue }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<ApiResponse<AssetType[]>>(
        `${API_ENDPOINTS.assets}?UserId=${userId}&organisationId=${organisationId}&Search=${searchValue}`
      );
      return data.results.flat();
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

interface AssetIncidentCheckProps {
  asset: AssetType;
  userId: string;
  organisationId: string;
}

// Thunk for checking if an asset has open incidents
export const checkAssetForOpenIncidents = createAsyncThunk<IncidentType[], AssetIncidentCheckProps>(
  "assets/checkAssetForOpenIncidents",
  async ({ asset, userId, organisationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<ApiResponse<IncidentType>>(
        `${API_ENDPOINTS.incidents}?UserId=${userId}&OrganisationId=${organisationId}&AssetClientReference=${asset.client.reference}&IncidentStatus=${Status.OPEN}`
      );
      return data.results || [];
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

const assetsSlice = createSlice({
  name: "assets",
  initialState,
  reducers: {
    setAsset: (state, action) => {
      state.assets = action.payload;
    },
    clearAssetError: (state) => {
      return {
        ...state,
        error: null,
      };
    },
    clearAssets: (state) => {
      state.assets = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAssets.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAssets.fulfilled, (state, action) => {
      state.loading = false;
      state.assets = action.payload;
    });
    builder.addCase(fetchAssets.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });

    // Handle checkAssetForOpenIncidents async thunk
    builder.addCase(checkAssetForOpenIncidents.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(checkAssetForOpenIncidents.fulfilled, (state, action) => {
      state.loading = false;
      // Note: We don't store the open incidents in the asset slice state
      // This thunk is used for checking purposes only
    });
    builder.addCase(checkAssetForOpenIncidents.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as ErrorType;
    });
  },
});

export const { clearAssets, clearAssetError } = assetsSlice.actions;
export const actionRootState = (state: RootState) => state;
export default assetsSlice.reducer;
